import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const { updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const { data } = await axios.post(
          `${API}/auth/login`,
          { email, password, remember_me: rememberMe },
          { withCredentials: true }
        );
        updateUser(data);
        toast.success('Welcome back!');
        const from = location.state?.from || '/dashboard';
        navigate(from, { replace: true });
      } else {
        const { data } = await axios.post(
          `${API}/auth/register`,
          { email, password, name },
          { withCredentials: true }
        );
        updateUser(data);
        toast.success('Account created! Please verify your email.');
        if (data.verification_link) {
          toast('Verification link (dev): ' + data.verification_link.substring(0, 60) + '...', {
            duration: 10000,
          });
          console.log('Email verification link:', data.verification_link);
        }
        navigate('/dashboard', { replace: true });
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (e.message || 'Something went wrong');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <BookOpen className="w-12 h-12 text-[#C53030]" />
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1C1917] mb-2">EduRepository</h1>
          <p className="text-base text-stone-800 leading-relaxed">Create professional lesson plans in minutes</p>
        </div>

        <Card className="p-8 border border-stone-200">
          <h2 className="text-xl sm:text-2xl text-[#1C1917] mb-6">{isLogin ? 'Welcome Back' : 'Get Started'}</h2>

          <Button
            type="button"
            data-testid="google-signin-button"
            onClick={startGoogleLogin}
            variant="outline"
            className="w-full border border-stone-300 hover:bg-stone-50 mb-4 flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
              <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2c-2 1.4-4.6 2.3-7.3 2.3-5.2 0-9.7-3.3-11.3-8L6 32.6C9.3 39.4 16.1 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.6l6.3 5.2C40.9 35.7 44 30.3 44 24c0-1.2-.1-2.3-.4-3.5z"/>
            </svg>
            <span className="text-sm font-medium text-stone-700">Continue with Google</span>
          </Button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-stone-200" /></div>
            <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-stone-500">or</span></div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="mt-1"
              />
            </div>

            {isLogin && (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="remember-me"
                    data-testid="remember-me-checkbox"
                    checked={rememberMe}
                    onCheckedChange={setRememberMe}
                  />
                  <Label htmlFor="remember-me" className="text-sm cursor-pointer">Remember me</Label>
                </div>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => navigate('/forgot-password')}
                  className="text-sm text-[#C53030] hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <Button
              data-testid="submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
            >
              {loading ? 'Please wait...' : isLogin ? 'Log In' : 'Sign Up'}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-stone-600">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              data-testid="toggle-auth-mode"
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#C53030] hover:underline font-medium"
            >
              {isLogin ? 'Sign up' : 'Log in'}
            </button>
          </p>
        </Card>
      </div>
    </div>
  );
};

export default Login;
